import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Plus, Trash2, Calendar, Clock, MapPin, Users, Loader2, CalendarDays, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  ongoing: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  completed: 'bg-[hsl(var(--surface-3))] text-muted-foreground',
  cancelled: 'bg-red-100 text-red-600 border-red-200'
};

const EventDialog = ({ open, onOpenChange, channelId, dmId }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [allUsers, setAllUsers] = useState([]);

  // Form
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [location, setLocation] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [creating, setCreating] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (channelId) params.set('channel_id', channelId);
      if (dmId) params.set('dm_id', dmId);
      const res = await axios.get(`${API}/events?${params}`);
      setEvents(res.data.events || []);
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [channelId, dmId]);

  useEffect(() => {
    if (open) {
      loadEvents();
      axios.get(`${API}/users`).then(res => setAllUsers(res.data.users || [])).catch(() => {});
    }
  }, [open, loadEvents]);

  const handleCreate = async () => {
    if (!title.trim() || !startAt || !endAt) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/events`, {
        title: title.trim(),
        description: description.trim(),
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        channel_id: channelId || null,
        dm_id: dmId || null,
        attendee_ids: attendees,
        location: location.trim()
      });
      setEvents(prev => [res.data.event, ...prev].sort((a, b) => new Date(a.start_at) - new Date(b.start_at)));
      resetForm();
      setShowCreate(false);
      toast.success('Event scheduled');
    } catch (e) {
      toast.error('Failed to create event');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setTitle(''); setDescription(''); setStartAt(''); setEndAt(''); setLocation(''); setAttendees([]);
  };

  const handleDelete = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents(prev => prev.filter(e => e.event_id !== eventId));
      toast.success('Event deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleStatus = async (eventId, status) => {
    try {
      await axios.put(`${API}/events/${eventId}`, { status });
      setEvents(prev => prev.map(e => e.event_id === eventId ? { ...e, status } : e));
    } catch (e) {
      toast.error('Failed to update event');
    }
  };

  const toggleAttendee = (userId) => {
    setAttendees(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const formatDateShort = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  const isUpcoming = (e) => new Date(e.start_at) > new Date() && e.status === 'scheduled';
  const isPast = (e) => new Date(e.end_at) < new Date();

  const upcomingEvents = events.filter(e => e.status === 'scheduled' && !isPast(e));
  const pastEvents = events.filter(e => e.status !== 'scheduled' || isPast(e));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[80vh] flex flex-col" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <CalendarDays className="w-5 h-5 text-[hsl(var(--primary))]" />
            Events
          </DialogTitle>
        </DialogHeader>

        {!showCreate ? (
          <>
            <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={() => setShowCreate(true)} data-testid="new-event-btn">
              <Plus className="w-3.5 h-3.5" /> Schedule Event
            </Button>

            <ScrollArea className="flex-1 max-h-[400px]">
              {loading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
              ) : (
                <div className="space-y-3">
                  {upcomingEvents.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Upcoming</p>
                      <div className="space-y-1.5">
                        {upcomingEvents.map(ev => (
                          <div key={ev.event_id} className="p-3 rounded-lg border border-border hover:bg-[hsl(var(--message-hover))] transition-colors" data-testid={`event-item-${ev.event_id}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{ev.title}</p>
                                {ev.description && <p className="text-xs text-muted-foreground mt-0.5">{ev.description}</p>}
                              </div>
                              <Badge className={`${statusColors[ev.status] || statusColors.scheduled} text-[10px] h-5 border flex-shrink-0`}>
                                {ev.status}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {formatDateShort(ev.start_at)}
                              </span>
                              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                <ChevronRight className="w-3 h-3" />
                                {formatDateShort(ev.end_at)}
                              </span>
                              {ev.location && (
                                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />{ev.location}
                                </span>
                              )}
                              {ev.attendee_ids?.length > 0 && (
                                <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                                  <Users className="w-3 h-3" />{ev.attendee_ids.length} attendee{ev.attendee_ids.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 mt-2">
                              {ev.created_by === user?.user_id && (
                                <>
                                  <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => handleStatus(ev.event_id, 'completed')} data-testid={`complete-event-${ev.event_id}`}>
                                    Complete
                                  </Button>
                                  <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={() => handleStatus(ev.event_id, 'cancelled')} data-testid={`cancel-event-${ev.event_id}`}>
                                    Cancel
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={() => handleDelete(ev.event_id)} data-testid={`delete-event-${ev.event_id}`}>
                                    <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {pastEvents.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">Past & Completed</p>
                      <div className="space-y-1">
                        {pastEvents.map(ev => (
                          <div key={ev.event_id} className="p-2.5 rounded-lg border border-border opacity-60" data-testid={`past-event-${ev.event_id}`}>
                            <div className="flex items-center gap-2">
                              <p className="text-sm flex-1 truncate">{ev.title}</p>
                              <Badge className={`${statusColors[ev.status] || statusColors.completed} text-[10px] h-4 border`}>
                                {ev.status}
                              </Badge>
                            </div>
                            <span className="text-[10px] text-muted-foreground">{formatDateShort(ev.start_at)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {events.length === 0 && (
                    <div className="text-center py-8">
                      <CalendarDays className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                      <p className="text-sm text-muted-foreground">No events scheduled</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Schedule one to keep your team in sync</p>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </>
        ) : (
          <>
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-3 pr-2">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Title</label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Event name..." className="h-9 text-sm" data-testid="event-title-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                  <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Details..." className="text-sm resize-none" rows={2} data-testid="event-desc-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Start</label>
                    <Input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="h-9 text-sm" data-testid="event-start-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">End</label>
                    <Input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="h-9 text-sm" data-testid="event-end-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Location</label>
                  <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="Room, link, or address..." className="h-9 text-sm" data-testid="event-location-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Attendees</label>
                  <div className="border border-border rounded-lg max-h-[120px] overflow-y-auto p-1">
                    {allUsers.filter(u => u.user_id !== user?.user_id).map(u => (
                      <button
                        key={u.user_id}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm transition-colors ${attendees.includes(u.user_id) ? 'bg-[hsl(var(--primary)/0.10)]' : 'hover:bg-[hsl(var(--message-hover))]'}`}
                        onClick={() => toggleAttendee(u.user_id)}
                        data-testid={`attendee-toggle-${u.user_id}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${attendees.includes(u.user_id) ? 'bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]' : 'border-border'}`}>
                          {attendees.includes(u.user_id) && '✓'}
                        </div>
                        <span className="flex-1 text-left truncate">{u.display_name || u.username}</span>
                      </button>
                    ))}
                  </div>
                  {attendees.length > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-1">{attendees.length} selected</p>
                  )}
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" onClick={() => { resetForm(); setShowCreate(false); }} data-testid="cancel-event-btn">Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={!title.trim() || !startAt || !endAt || creating} data-testid="save-event-btn">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CalendarDays className="w-3.5 h-3.5 mr-1" />}
                Schedule
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventDialog;
